import { Typography } from "@mui/material";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import SpacedGridContainer from "~/components/SpacedGridContainer";
import SpacedGridItem from "~/components/SpacedGridItem";
import SelectSite from "~/components/select/SelectSite";
import { type LocationDocument } from "~/gql/graphqlBase.generated";
import { useAppDispatch, useAppSelector } from "~/hooks/reduxHooks";
import useIsSiteRequiredRoute from "~/hooks/useIsSiteRequiredRoute";
import { updateSite } from "~/store/siteSlice";

interface RequireSiteProps {
	children: React.ReactNode;
}

function RequireSite({ children }: RequireSiteProps) {
	const { t } = useTranslation("common");
	const site = useAppSelector((state) => state.site);
	const currentTenantId = useAppSelector((state) => state.settings.tenantId);
	const dispatch = useAppDispatch();
	const required = useIsSiteRequiredRoute();

	const handleInputChange = (val: LocationDocument | null) => {
		dispatch(updateSite(val));
	};

	const hasSite = Boolean(site && site.location && site.location.id);
	// No tenant stamp, or tenantId not yet resolved on this render, means "unknown" — treated as valid so a cold boot doesn't force a reselect.
	const siteBelongsToCurrentTenant = !site?.tenantId || !currentTenantId || site.tenantId === currentTenantId;
	const siteValid = hasSite && siteBelongsToCurrentTenant;

	useEffect(() => {
		if (required && hasSite && !siteBelongsToCurrentTenant) {
			dispatch(updateSite(null));
		}
	}, [required, hasSite, siteBelongsToCurrentTenant]);

	if (!required || siteValid) {
		return <>{children}</>;
	}

	return (
		<SpacedGridContainer>
			<SpacedGridItem maxCols={1} smMargin>
				<Typography component="h5" variant="h5">
					{t("shell.requireSiteTitle")}
				</Typography>
			</SpacedGridItem>
			<SpacedGridItem maxCols={1} smMargin>
				<SelectSite handleInputChange={handleInputChange} />
			</SpacedGridItem>
		</SpacedGridContainer>
	);
}

export default RequireSite;
